// ─────────────────────────────────────────────
//  PortfolioSummaryCard.js
// ─────────────────────────────────────────────
import React from "react";
import { useNavigate } from "react-router-dom";
import { usePortfolio } from "../../hooks/usePortfolio";
import "./PortfolioSummaryCard.css";

function fmt(n) {
  if (Math.abs(n) >= 1e7) return "₹" + (n / 1e7).toFixed(2) + " Cr";
  if (Math.abs(n) >= 1e5) return "₹" + (n / 1e5).toFixed(2) + " L";
  return "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

export default function PortfolioSummaryCard() {
  const { holdings, summary, liveData, loading } = usePortfolio();
  const navigate = useNavigate();

  const isUp  = summary.totalPnL >= 0;
  const hasHoldings = holdings.length > 0;

  if (loading) {
    return (
      <div className="psc-wrap skeleton">
        <div className="psc-skel-line w60" />
        <div className="psc-skel-line w40" />
        <div className="psc-skel-line w80" />
      </div>
    );
  }

  if (!hasHoldings) {
    return (
      <div className="psc-wrap empty">
        <div className="psc-empty-icon">💼</div>
        <div className="psc-empty-title">No Holdings Yet</div>
        <div className="psc-empty-sub">Add your first stock in the Portfolio section</div>
        <button className="psc-btn" onClick={() => navigate("/portfolio")}>
          Go to Portfolio →
        </button>
      </div>
    );
  }

  // Top 3 holdings by current value
  const top3 = [...holdings]
    .map((h) => {
      const cmp      = liveData[h.symbol]?.price || h.avg_buy_price;
      const current  = cmp * h.qty;
      const invested = h.avg_buy_price * h.qty;
      const pnl      = current - invested;
      const pnlPct   = invested ? (pnl / invested) * 100 : 0;
      return { ...h, cmp, current, pnl, pnlPct };
    })
    .sort((a, b) => b.current - a.current)
    .slice(0, 3);

  return (
    <div className="psc-wrap">
      {/* Header */}
      <div className="psc-header">
        <span className="psc-label">Portfolio P&amp;L</span>
        <button className="psc-view-btn" onClick={() => navigate("/portfolio")}>
          View All →
        </button>
      </div>

      {/* Main numbers */}
      <div className="psc-main">
        <div className="psc-current">{fmt(summary.totalCurrent)}</div>
        <div className={`psc-pnl ${isUp ? "up" : "down"}`}>
          <span className="psc-arrow">{isUp ? "▲" : "▼"}</span>
          {fmt(Math.abs(summary.totalPnL))}
          <span className="psc-pct">({Math.abs(summary.totalPnLPercent)}%)</span>
        </div>
      </div>

      {/* Sub metrics */}
      <div className="psc-metrics">
        <div className="psc-metric">
          <span className="psc-metric-label">Invested</span>
          <span className="psc-metric-val">{fmt(summary.totalInvested)}</span>
        </div>
        <div className="psc-metric">
          <span className="psc-metric-label">Stocks</span>
          <span className="psc-metric-val">{holdings.length}</span>
        </div>
        <div className="psc-metric">
          <span className="psc-metric-label">Day's P&L</span>
          <span className="psc-metric-val text-muted">—</span>
        </div>
      </div>

      {/* Top 3 holdings mini-list */}
      <div className="psc-divider" />
      <div className="psc-holdings">
        {top3.map((h) => (
          <div className="psc-holding-row" key={h.symbol}>
            <span className="psc-h-symbol">{h.symbol}</span>
            <span className="psc-h-qty">{h.qty} qty</span>
            <span className={`psc-h-pnl ${h.pnl >= 0 ? "up" : "down"}`}>
              {h.pnl >= 0 ? "▲" : "▼"} {Math.abs(h.pnlPct).toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
